import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import {
    add,
    format,
    isSameMonth,
    isToday,
    parse,
    startOfToday,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    startOfMonth,
    startOfWeek,
    isSameDay,
} from "date-fns"
import { cn } from "../../lib/utils"
// Note: Depending on project structure, import might be "@/lib/utils" or "../../../lib/utils" 
// The popover.tsx used "@/lib/utils", so I'll try that first to be consistent with existing UI components if tsconfig allows, 
// but since I am writing to src/frontend/components/ui/calendar.tsx, ../../lib/utils is safe relative path from there.
// Actually, looking at popover.tsx: `import { cn } from "@/lib/utils"`. I should stick to local Convention if I knew tsconfig paths. 
// `MemoEditor` imports `api` from `../lib/api`. 
// `utils` is at `src/frontend/lib/utils.ts`. 
// So from `src/frontend/components/ui/calendar.tsx`, `../../lib/utils` is correct. 

export type CalendarProps = {
    mode?: "single"
    selected?: Date
    onSelect?: (date: Date | undefined) => void
    className?: string
}

export function Calendar({
    selected,
    onSelect,
    className,
}: CalendarProps) {
    let today = startOfToday()
    let [currentMonth, setCurrentMonth] = React.useState(format(today, "MMM-yyyy"))
    let firstDayCurrentMonth = parse(currentMonth, "MMM-yyyy", new Date())

    let days = eachDayOfInterval({
        start: startOfWeek(startOfMonth(firstDayCurrentMonth)),
        end: endOfWeek(endOfMonth(firstDayCurrentMonth)),
    })

    function previousMonth() {
        let firstDayNextMonth = add(firstDayCurrentMonth, { months: -1 })
        setCurrentMonth(format(firstDayNextMonth, "MMM-yyyy"))
    }

    function nextMonth() {
        let firstDayNextMonth = add(firstDayCurrentMonth, { months: 1 })
        setCurrentMonth(format(firstDayNextMonth, "MMM-yyyy"))
    }

    return (
        <div className={cn("p-3", className)}>
            <div className="flex items-center justify-between pl-2 pr-2 mb-4">
                <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                    {format(firstDayCurrentMonth, "MMMM yyyy")}
                </span>
                <div className="flex items-center space-x-1">
                    <button
                        onClick={previousMonth}
                        type="button"
                        className="p-1.5 text-zinc-400 hover:text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md transition-colors"
                    >
                        <span className="sr-only">Previous month</span>
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                        onClick={nextMonth}
                        type="button"
                        className="p-1.5 text-zinc-400 hover:text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md transition-colors"
                    >
                        <span className="sr-only">Next month</span>
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-7 text-xs leading-6 text-center text-zinc-500 dark:text-zinc-400 mb-2">
                <div>Su</div>
                <div>Mo</div>
                <div>Tu</div>
                <div>We</div>
                <div>Th</div>
                <div>Fr</div>
                <div>Sa</div>
            </div>
            <div className="grid grid-cols-7 text-sm">
                {days.map((day, dayIdx) => {
                    const isSelected = selected ? isSameDay(day, selected) : false
                    const isCurrentMonth = isSameMonth(day, firstDayCurrentMonth)

                    return (
                        <div
                            key={day.toString()}
                            className={cn(
                                "py-1",
                                dayIdx === 0 && "col-start-1" // Actually grid-cols-7 handles this, but keeping structure simpl simple
                            )}
                        >
                            <button
                                type="button"
                                onClick={() => onSelect?.(day)}
                                className={cn(
                                    "mx-auto flex h-8 w-8 items-center justify-center rounded-full transition-colors",
                                    isSelected && "text-white",
                                    !isSelected && isCurrentMonth && "text-zinc-900 dark:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800",
                                    !isSelected && !isCurrentMonth && "text-zinc-400 dark:text-zinc-600 hover:bg-zinc-50 dark:hover:bg-zinc-900",
                                    isSelected && isToday(day) && "bg-zinc-900 dark:bg-zinc-50 dark:text-zinc-900",
                                    isSelected && !isToday(day) && "bg-zinc-900 dark:bg-zinc-50 dark:text-zinc-900",
                                    !isSelected && isToday(day) && "text-blue-500 font-semibold bg-blue-50 dark:bg-blue-900/20",
                                )}
                            >
                                <time dateTime={format(day, "yyyy-MM-dd")}>
                                    {format(day, "d")}
                                </time>
                            </button>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
